import { useEffect, useId, useState, type FormEvent, type ReactNode } from 'react';
import { CONNECTIONS, SITE_TYPES, config, origin, type Connection, type SiteType } from '../config';
import { t as translate } from '../i18n';
import { useHref, useLang } from '../lang';
import { PATHS, type Line } from '../routes';

type Power = 'battery' | 'mains' | 'solar' | 'poe' | 'unsure';
type BinPlace = 'indoor' | 'outdoor';
type Cameras = 'yes' | 'some' | 'unsure';
type Status = 'idle' | 'sending' | 'ok' | 'mailto';

const LINES: Line[] = ['waste', 'vision', 'both'];
const POWER: Power[] = ['unsure', 'battery', 'mains', 'solar', 'poe'];
const CAMERAS: Cameras[] = ['yes', 'some', 'unsure'];

function lineFromQuery(): Line | null {
  const v = new URLSearchParams(window.location.search).get('line');
  return v === 'waste' || v === 'vision' || v === 'both' ? v : null;
}

export function DemoForm() {
  const { t, lang } = useLang();
  const href = useHref();
  const [line, setLine] = useState<Line | null>(null);
  const [company, setCompany] = useState('');
  const [contact, setContact] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [siteType, setSiteType] = useState<SiteType | ''>('');
  const [binPlace, setBinPlace] = useState<BinPlace>('outdoor');
  const [power, setPower] = useState<Power>('unsure');
  const [conn, setConn] = useState<Connection>('unsure');
  const [cameras, setCameras] = useState<Cameras>('yes');
  const [areas, setAreas] = useState('');
  const [notes, setNotes] = useState('');
  const [hp, setHp] = useState('');
  const [status, setStatus] = useState<Status>('idle');

  // Read after hydration so the prerendered form and the first client render match.
  useEffect(() => {
    const q = lineFromQuery();
    if (q) setLine(q);
  }, []);

  const wantsWaste = line === 'waste' || line === 'both';
  const wantsVision = line === 'vision' || line === 'both';

  // The onboarding API has no line field yet, so the line leads the notes.
  const payload = () => {
    const lineName = line ? translate('en', line === 'both' ? 'cta.line.both' : `line.${line}`) : 'unspecified';
    const parts = [
      `Line: ${line === 'both' ? 'Iris Waste and Iris Vision' : lineName}.`,
      `[iris landing · ${lang}]`,
      wantsWaste ? `Waste: bin ${binPlace}, power ${power}, connection ${conn}.` : '',
      wantsVision ? `Vision: cameras installed ${cameras}.${areas.trim() ? ` Areas: ${areas.trim()}.` : ''}` : '',
      notes.trim(),
    ];
    return {
      company: company.trim(),
      contactName: contact.trim(),
      email: email.trim(),
      phone: phone.trim() || null,
      siteType: siteType || 'other',
      sensorCount: 1,
      chuteBins: 0,
      freestandingBins: 0,
      placement: wantsWaste ? binPlace : 'both',
      connectivity: [wantsWaste ? conn : 'unsure'],
      notes: parts.filter(Boolean).join(' '),
      source: config.formSource || 'banner',
    };
  };

  const mailtoHref = () => {
    const d = payload();
    const subject = encodeURIComponent(`IRIS demo request - ${d.company || 'new site'}`);
    const body = encodeURIComponent(
      [
        `Company: ${d.company}`,
        `Contact: ${d.contactName}`,
        `Email: ${d.email}`,
        `Phone: ${d.phone ?? ''}`,
        `Site type: ${d.siteType}`,
        d.notes,
        `Page: ${origin()}${PATHS.demo}`,
      ].join('\n'),
    );
    return `mailto:${config.salesEmail}?subject=${subject}&body=${body}`;
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (hp) {
      setStatus('ok');
      return;
    }
    setStatus('sending');
    if (config.formEndpoint) {
      try {
        const res = await fetch(config.formEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload()),
        });
        if (res.ok) {
          setStatus('ok');
          return;
        }
      } catch {
        /* network or CORS failure falls back to mail */
      }
    }
    window.location.href = mailtoHref();
    setStatus('mailto');
  };

  return (
    <form className="form" id="request" onSubmit={onSubmit}>
      <fieldset className="line-choice wide">
        <legend>{t('cta.line')}</legend>
        <div className="line-options">
          {LINES.map((l) => (
            <label key={l} className="line-option">
              <input
                type="radio"
                name="line"
                value={l}
                required
                checked={line === l}
                onChange={() => setLine(l)}
              />
              <span className="line-option-name">{t(`cta.line.${l}`)}</span>
              <span className="line-option-d">{t(`cta.line.${l}.d`)}</span>
            </label>
          ))}
        </div>
        {!line && <p className="field-hint">{t('cta.line.pick')}</p>}
      </fieldset>

      {wantsWaste && (
        <fieldset className="group wide">
          <legend>{t('cta.group.waste')}</legend>
          <div className="group-fields">
          <Field label={t('cta.placement')}>
            {(id) => (
              <select id={id} name="placement" value={binPlace} onChange={(e) => setBinPlace(e.target.value as BinPlace)}>
                {(['outdoor', 'indoor'] as const).map((p) => (
                  <option key={p} value={p}>
                    {t(`cta.place.${p}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('cta.power')}>
            {(id) => (
              <select id={id} name="power" value={power} onChange={(e) => setPower(e.target.value as Power)}>
                {POWER.map((p) => (
                  <option key={p} value={p}>
                    {t(`cta.power.${p}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('cta.conn')} hint={t('cta.hint.waste')} wide>
            {(id, hint) => (
              <select
                id={id}
                aria-describedby={hint}
                name="connectivity"
                value={conn}
                onChange={(e) => setConn(e.target.value as Connection)}
              >
                {CONNECTIONS.map((c) => (
                  <option key={c} value={c}>
                    {t(`cta.conn.${c}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          </div>
        </fieldset>
      )}

      {wantsVision && (
        <fieldset className="group wide">
          <legend>{t('cta.group.vision')}</legend>
          <div className="group-fields">
          <Field label={t('cta.cameras')} hint={t('cta.hint.vision')} wide>
            {(id, hint) => (
              <select
                id={id}
                aria-describedby={hint}
                name="cameras"
                value={cameras}
                onChange={(e) => setCameras(e.target.value as Cameras)}
              >
                {CAMERAS.map((c) => (
                  <option key={c} value={c}>
                    {t(`cta.cameras.${c}`)}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label={t('cta.areas')} wide>
            {(id) => (
              <input
                id={id}
                name="areas"
                autoComplete="off"
                maxLength={300}
                placeholder={t('cta.areas.ph')}
                value={areas}
                onChange={(e) => setAreas(e.target.value)}
              />
            )}
          </Field>
          </div>
        </fieldset>
      )}

      <fieldset className="group wide">
        <legend>{t('cta.group.site')}</legend>
          <div className="group-fields">
        <Field label={t('cta.company')} hint={t('cta.hint.company')}>
          {(id, hint) => (
            <input
              id={id}
              aria-describedby={hint}
              required
              minLength={2}
              name="organization"
              autoComplete="organization"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cta.siteType')}>
          {(id) => (
            <select
              id={id}
              name="siteType"
              required
              value={siteType}
              onChange={(e) => setSiteType(e.target.value as SiteType | '')}
            >
              <option value="" disabled>
                {t('cta.site.choose')}
              </option>
              {SITE_TYPES.map((s) => (
                <option key={s} value={s}>
                  {t(`cta.site.${s}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t('cta.contact')}>
          {(id) => (
            <input
              id={id}
              required
              minLength={2}
              name="name"
              autoComplete="name"
              value={contact}
              onChange={(e) => setContact(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cta.email')} hint={t('cta.hint.email')}>
          {(id, hint) => (
            <input
              id={id}
              aria-describedby={hint}
              required
              type="email"
              name="email"
              autoComplete="email"
              spellCheck={false}
              dir="ltr"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cta.phone')}>
          {(id) => (
            <input
              id={id}
              type="tel"
              name="tel"
              autoComplete="tel"
              dir="ltr"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        <Field label={t('cta.notes')} wide>
          {(id) => (
            <textarea
              id={id}
              name="notes"
              autoComplete="off"
              rows={4}
              maxLength={3000}
              placeholder={t('cta.notesPh')}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          )}
        </Field>
        </div>
      </fieldset>

      <div className="hp" hidden aria-hidden="true">
        <label>
          {t('cta.hp')}
          <input tabIndex={-1} autoComplete="off" name="website" value={hp} onChange={(e) => setHp(e.target.value)} />
        </label>
      </div>
      <p className="form-next">{t('cta.next')}</p>
      <div className="form-foot">
        <button className="btn btn-primary" type="submit" disabled={status === 'sending'}>
          {status === 'sending' ? t('cta.sending') : t('cta.submit')}
        </button>
        <a className="link" href={`mailto:${config.salesEmail}`}>
          {t('cta.mailtoManual')}
        </a>
      </div>
      <div className="form-status" aria-live="polite">
        {status === 'ok' && <p className="ok">{t('cta.ok')}</p>}
        {status === 'mailto' && <p className="warn">{t('cta.mailto')}</p>}
      </div>
      <p className="form-privacy">
        {t('cta.privacy')}{' '}
        <a className="link" href={href(PATHS.privacy)}>
          {t('cta.privacyLink')}
        </a>
        {' · '}
        <a className="link" href={href(PATHS.terms)}>
          {t('nav.terms')}
        </a>
      </p>
    </form>
  );
}

function Field({
  label,
  hint,
  wide = false,
  children,
}: {
  label: string;
  hint?: string;
  wide?: boolean;
  children: (id: string, hintId: string | undefined) => ReactNode;
}) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  return (
    <div className={`field ${wide ? 'wide' : ''}`.trim()}>
      <label htmlFor={id}>{label}</label>
      {children(id, hintId)}
      {hint && (
        <p className="field-hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}
